import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, skipWhile, tap } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { MESSAGES } from 'src/app/core/constants/messages';
import { REGEX } from 'src/app/core/constants/regex';
import { FeedbackService } from 'src/app/core/services/feedback/feedback.service';
import { GetBooleanFromString } from '../../utils/get-boolean-from-string';
import { StringCanBeNumber } from '../../utils/string-can-be-number';
import { InputFieldComponent } from '../fields/input-field/input-field.component';
import { RangeSelectionDirective } from './range-selection/range-selection.directive';
import { RangeSelection } from './range-selection/range-selection.interface';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TableComponent implements AfterContentInit, OnChanges, OnDestroy {
  @Input() allowSelection = true;
  @Input() allowPaste = true;
  @Input() allowMultipleEdition = true;
  @Input() enableAddControl = true;
  @Input() enableDeleteControl = true;
  @Input() cellsPadding = true;
  @Input() uppercasedTitles = true;
  @Input() secondaryTheme = false;
  @Input() titleWrap = true;

  @Input() tableForm: UntypedFormArray;
  @Input() tableFormNestedOffset: string[];
  @Input() tableConfig: any;

  @Output() add = new EventEmitter<{ row: any; index: number }>();
  @Output() delete = new EventEmitter<{ row: any; index: number }>();
  @Output() tablePaste = new EventEmitter<{
    fieldName: string;
    field: AbstractControl;
    value: any;
    isLast: boolean;
  }>();
  @Output() unhandledPaste = new EventEmitter<{
    fieldName: string;
    field: AbstractControl;
    value: any;
    isLast: boolean;
  }>();
  @Output() selectCell = new EventEmitter<RangeSelection>();
  @Output() selectMultipleCells = new EventEmitter<RangeSelection>();

  @ViewChild(RangeSelectionDirective) table: any;
  @ViewChild('header') header: ElementRef;

  public cacheHoverState: boolean;
  public currentHover: any;
  public currentHoverIndex: number;

  controlsOffset: number;
  controlsVisibleState: boolean;
  forcedHiddenControls: boolean;

  templateClasses: any;
  tableRowClass: string;

  currentSelection: RangeSelection;
  selectionDebounceTime = 300;
  selectedFieldsArray: Array<{
    fieldName: string;
    field: AbstractControl;
    pastable: boolean;
  }>;
  pastedFieldsLength = 0;
  public pasteMaskError = false;

  selectionStream$ = new Subject<RangeSelection>();
  selectedMemberChange$ = new Subject();
  ignoreNextChangeByPasteEvent$ = new BehaviorSubject(false);
  selectedMemberChangeSubs = new Subscription();
  selectionStreamSubs = new Subscription();

  constructor(private feedbackService: FeedbackService) {
    this.cacheHoverState = false;
    this.currentHover = null;
    this.currentHoverIndex = null;
    this.controlsVisibleState = false;
    this.forcedHiddenControls = false;
    this.controlsOffset = 0;
    this.templateClasses = {
      secondary: this.secondaryTheme,
      'no-wrap-titles': !this.titleWrap,
      'anflex-table--uppercased-titles': this.uppercasedTitles,
      'anflex-table--has-add-control': this.enableAddControl,
      'anflex-table--has-delete-control': this.enableDeleteControl,
    };
    this.tableRowClass = 'anflex-table-body-row';
  }

  ngAfterContentInit(): void {
    this.defineInitialOffset();
    this.updateTemplateClasses();
    this.watchSelectionEvents();
  }

  ngOnChanges(): void {
    this.updateTemplateClasses();
  }

  ngOnDestroy(): void {
    this.selectionStreamSubs.unsubscribe();
    this.selectedMemberChangeSubs.unsubscribe();
  }

  public showControls(event, row, rowIndex?): void {
    if (this.enableAddControl || this.enableDeleteControl) {
      const rowElement: any = event
        .composedPath()
        ?.find((el) => el?.className?.includes(this.tableRowClass));
      const hoveredElement: any = rowElement || event.target?.parentElement;
      const offset: number = hoveredElement ? hoveredElement.offsetTop : 0;
      this.setControlsOffset(offset);
      this.controlsVisibleState = true;
      this.currentHover = row;
      this.currentHoverIndex = rowIndex;
    }
  }

  public hideControls(): void {
    if (this.enableAddControl || this.enableDeleteControl) {
      this.controlsVisibleState = false;
    }
  }

  public isCurrentlyHovered(index): boolean {
    return this.cacheHoverState && this.currentHoverIndex === index;
  }

  setControlsOffset(offset: number): void {
    this.controlsOffset = offset ? offset : 0;
  }

  getControlsOffset(): number {
    return this.controlsOffset;
  }

  updateTemplateClasses(): void {
    this.templateClasses = {
      'anflex-table--uppercased-titles': this.uppercasedTitles,
      'anflex-table--has-add-control': this.enableAddControl,
      'anflex-table--has-delete-control': this.enableDeleteControl,
    };
  }

  defineInitialOffset(): void {
    this.setControlsOffset(this.header?.nativeElement.clientHeight);
  }

  triggerAddEvent(): void {
    this.add.emit({
      row: this.currentHover,
      index: this.currentHoverIndex,
    });
  }

  triggerDeleteEvent(): void {
    this.delete.emit({
      row: this.currentHover,
      index: this.currentHoverIndex,
    });
  }

  enableHoverStateCache(): void {
    this.cacheHoverState = true;
  }

  disableHoverStateCache(): void {
    this.cacheHoverState = false;
  }

  onPasteEvent(event: ClipboardEvent): void {
    if (!this.allowPaste || !this.tableForm || !this.tableConfig) {
      return;
    }
    event.preventDefault();
    this.pastedFieldsLength = 0;
    this.ignoreNextChangeByPasteEvent$.next(true);
    const { clipboardData } = event;
    const pastedText = clipboardData.getData(CONSTANTS.CLIPBOARD_TEXT);
    const parsedPasteData = this.parsePasteInstrunctions(pastedText);
    this.pasteTableValues(parsedPasteData);
    this.table.clearCells();
    this.warnPasteError();
  }

  warnPasteError(): void {
    if (this.pasteMaskError) {
      this.feedbackService.warning(MESSAGES.WARNING.PASTE_WRONG_FORMAT);
    }

    this.pasteMaskError = false;
  }

  detectRowDivisor(rowsString: string): string {
    if (rowsString.indexOf(REGEX.CLIPBOARD_ROWS_DIVISORS.WINDOWS) !== -1) {
      return REGEX.CLIPBOARD_ROWS_DIVISORS.WINDOWS;
    }
    if (rowsString.indexOf(REGEX.CLIPBOARD_ROWS_DIVISORS.POSIX) !== -1) {
      return REGEX.CLIPBOARD_ROWS_DIVISORS.POSIX;
    }
    return REGEX.CLIPBOARD_ROWS_DIVISORS.MAC_POSIX;
  }

  parsePasteInstrunctions(pastedText: string): any[][] | string {
    if (!this.isPastedTableData(pastedText)) {
      return pastedText;
    }

    const rowDivisor = this.detectRowDivisor(pastedText);
    const rowsString = pastedText.split(rowDivisor).filter((str) => str !== '');
    return rowsString.map((row: string) => {
      return row.split(REGEX.CLIPBOARD_COLS_DIVISOR);
    });
  }

  isPastedTableData(pastedText: string): boolean {
    return (
      pastedText.includes(this.detectRowDivisor(pastedText)) ||
      pastedText.includes(REGEX.CLIPBOARD_COLS_DIVISOR)
    );
  }

  pasteTableValues(pastedData: any[][] | string): void {
    const simpleString = typeof pastedData === 'string';

    if (simpleString) {
      this.pasteByFormField(pastedData);
      return;
    }

    const singleString = pastedData.length === 1 && pastedData[0].length === 1;
    if (singleString) {
      this.pasteByFormField(pastedData[0][0]);
      return;
    }

    this.pasteByData(pastedData);
  }

  pasteByFormField(value: string): void {
    this.selectedFieldsArray.forEach(({ fieldName, field, pastable }, index) => {
      const isBasicPastable = this.isBasicPastableField(this.tableConfig[fieldName]);
      const isLast = index === this.selectedFieldsArray.length - 1;

      if (pastable && isBasicPastable) {
        this.writeNewOrLastValues(
          field as AbstractControl & { accessor: InputFieldComponent },
          value,
          isLast,
        );
      } else {
        this.unhandledPaste.emit({ fieldName, field, value, isLast });
      }
      this.tablePaste.emit({ fieldName, field, value, isLast });
    });
  }

  getFieldConsideringNestedOffset(
    rowForm: UntypedFormGroup,
    relatedFieldName: string,
  ): AbstractControl {
    let field;
    if (this.tableFormNestedOffset) {
      this.tableFormNestedOffset.forEach((fieldName, index) => {
        if (!index) {
          field = rowForm.get(fieldName) as UntypedFormArray;
        } else {
          field = field.get(fieldName) as UntypedFormArray;
        }
      });

      return field.get(relatedFieldName) as AbstractControl;
    }
    return rowForm.get(relatedFieldName) as AbstractControl;
  }

  pasteByData(pastedData: any[][]): void {
    const rowFieldNames = Object.keys(this.tableConfig);
    const visibleFieldNames = rowFieldNames.filter((fieldName) =>
      this.isFieldVisible(fieldName, this.tableConfig),
    );

    if (pastedData.length) {
      this.pastedFieldsLength = pastedData.reduce((acc, row) => (acc += row.length), 1);
    } else {
      this.pastedFieldsLength = 0;
    }

    pastedData.forEach((pastedRow, rowIndex) => {
      if (this.tableForm?.controls[this.minorSelectedRowIndex + rowIndex]) {
        this.pasteEachData(pastedRow, rowIndex, pastedData, visibleFieldNames);
      }
    });
  }

  pasteEachData(
    pastedRow: any[],
    rowIndex: number,
    pastedData: any[][],
    visibleFieldNames: string[],
  ): void {
    const rowForm = this.tableForm.controls[
      this.minorSelectedRowIndex + rowIndex
    ] as UntypedFormGroup;
    const isLastCell = rowIndex === pastedData.length - 1;

    pastedRow.forEach((cellValue, cellIndex) => {
      const relatedFieldName = visibleFieldNames[this.minorSelectedCellIndex + cellIndex];
      const parsedCellValue = StringCanBeNumber(cellValue) ? +cellValue : cellValue;
      const isPastable = this.isFieldPastable(relatedFieldName, this.tableConfig);
      const isBasicPastable = this.isBasicPastableField(this.tableConfig[relatedFieldName]);
      const isLastRow = cellIndex === pastedRow.length - 1;
      const isLast = isLastRow && isLastCell;
      const field = this.getFieldConsideringNestedOffset(
        rowForm,
        relatedFieldName,
      ) as AbstractControl & { accessor: InputFieldComponent };

      if (isPastable && isBasicPastable) {
        this.writeNewOrLastValues(field, parsedCellValue, isLast);
      } else {
        this.unhandledPaste.emit({
          fieldName: relatedFieldName,
          field,
          value: parsedCellValue,
          isLast,
        });
      }

      this.tablePaste.emit({
        fieldName: relatedFieldName,
        field,
        value: parsedCellValue,
        isLast,
      });
    });
  }

  public writeNewOrLastValues(
    field: AbstractControl & { accessor: InputFieldComponent },
    value: any,
    isLast: boolean,
  ): void {
    if (!field.accessor) {
      return;
    }

    const prevVal = field.value;
    field.accessor.writeValue(value);
    field.accessor.writeValue(field.accessor.maskRef.value);
    const valueAfterMask = +field.accessor.fieldValue || field.accessor.fieldValue;

    if (
      String(valueAfterMask) !== String(value) ||
      (valueAfterMask === '' && valueAfterMask !== value && valueAfterMask !== prevVal)
    ) {
      field.setValue(prevVal, { emitEvent: isLast });
      this.pasteMaskError = true;
    }

    this.pasteMaskError = false || this.pasteMaskError;
  }

  isFieldPastable(fieldName: string, tableConfig: any): boolean {
    return tableConfig[fieldName]?.pastable;
  }

  isFieldVisible(fieldName: string, tableConfig: any): boolean {
    return tableConfig[fieldName]?.visible;
  }

  isBasicPastableField(fieldConfig: any): boolean {
    return (
      !fieldConfig?.type ||
      fieldConfig.type === CONSTANTS.FIELD_TYPES.INPUT ||
      fieldConfig.type === CONSTANTS.FIELD_TYPES.TEXTAREA
    );
  }

  triggerSelectionEffects(event: RangeSelection): void {
    this.selectionStream$.next(event);
  }

  watchSelectionEvents(): void {
    this.selectionStreamSubs = this.selectionStream$
      .pipe(
        tap((event) => {
          this.ignoreNextChangeByPasteEvent$.next(false);
          this.pastedFieldsLength = 0;
          this.currentSelection = event;
          this.onSelectCell(this.currentSelection);
          this.selectCell.emit(this.currentSelection);
        }),
        debounceTime(this.selectionDebounceTime),
        tap((event) => {
          this.onSelectMultipleCells(event);
          this.selectMultipleCells.emit(event);
        }),
      )
      .subscribe();
  }

  onSelectCell(event: RangeSelection): void {
    this.currentSelection = event;
    this.updateSelectedFieldsArray();
  }

  onSelectMultipleCells(event: RangeSelection): void {
    this.onSelectCell(event);
    this.handleMultipleEdition();
  }

  handleMultipleEdition(): void {
    if (!this.currentSelection) {
      return;
    }
    this.updateSelectedFieldsArray();
    this.watchSelectedCellsChanges();
  }

  updateSelectedFieldsArray(): void {
    if (!this.currentSelection || !this.tableConfig || !this.tableForm) {
      this.selectedFieldsArray = [];
      return;
    }

    this.resetFormSubs();
    this.selectedFieldsArray = [...this.currentSelection.cells].map((cell) => {
      const row = cell.closest(CONSTANTS.ROW_TAG).rowIndex;
      const col = cell.cellIndex;
      const fieldName = Object.keys(this.tableConfig).filter(
        (name) => this.tableConfig[name].visible,
      )[col];

      if (!this.tableConfig[fieldName]) {
        return { fieldName, field: null, pastable: false };
      }

      const { pastable } = this.tableConfig[fieldName];
      const fieldConfig = this.tableConfig[fieldName];
      const shouldConsiderField = fieldConfig.visible && !fieldConfig.readOnly;
      const selectedField = this.getFieldConsideringNestedOffset(
        this.tableForm.get(row.toString()) as UntypedFormGroup,
        fieldName,
      ) as AbstractControl & { accessor: InputFieldComponent };

      if (!selectedField && !shouldConsiderField) {
        return null;
      }

      return { fieldName, field: selectedField, pastable };
    });
  }

  watchSelectedCellsChanges(): void {
    if (!this.allowMultipleEdition) {
      return;
    }

    this.selectedFieldsArray.forEach(({ field }: any) => {
      if (field) {
        const currFieldSubs = field.valueChanges
          .pipe(
            skipWhile(() => {
              const pastingOtherFields = this.pastedFieldsLength > 0;

              if (this.pastedFieldsLength > 0) {
                this.pastedFieldsLength--;
              }
              if (this.pastedFieldsLength === 0) {
                this.ignoreNextChangeByPasteEvent$.next(false);
              }
              return !!pastingOtherFields;
            }),
          )
          .subscribe((value) => {
            this.setOtherSelectedFieldValues(value);
          });
        this.selectedMemberChangeSubs.add(currFieldSubs);
      }
    });
  }

  resetFormSubs(): void {
    this.selectedMemberChangeSubs.unsubscribe();
    this.selectedMemberChangeSubs = new Subscription();
  }

  setOtherSelectedFieldValues(value: any): void {
    if (this.ignoreNextChangeByPasteEvent$.getValue() || this.pastedFieldsLength > 0) {
      return;
    }

    this.selectedFieldsArray.forEach(({ fieldName, field, pastable }: any, index) => {
      const isLast = index === this.selectedFieldsArray.length - 1;
      const valueChanged = field?.value !== value;

      if (pastable && valueChanged) {
        field?.setValue(value, { emitEvent: isLast });
      }

      if (!pastable && valueChanged) {
        this.unhandledPaste.emit({ fieldName, field, value, isLast });
      }

      this.ignoreNextChangeByPasteEvent$.next(false);
      this.pastedFieldsLength = 0;
    });
  }

  onCopyEvent(event: ClipboardEvent): void {
    if (!this.currentSelection?.cells?.size) {
      return;
    }
    event.preventDefault();

    const rangeArray = [...this.currentSelection.cells];
    let copyString = '';
    let lastRowIndex = rangeArray[0].closest(CONSTANTS.ROW_TAG).rowIndex;

    rangeArray.forEach((cell, index) => {
      const currRowIndex = cell.closest(CONSTANTS.ROW_TAG).rowIndex;
      let preffix = '';

      if (index) {
        preffix =
          currRowIndex === lastRowIndex
            ? REGEX.CLIPBOARD_COLS_DIVISOR
            : REGEX.CLIPBOARD_ROWS_DIVISORS.WINDOWS;
      }

      if (!cell.children.length) {
        copyString += `${preffix}${cell.innerText}`;
      } else {
        copyString += `${preffix}${this.getValueByCell(cell)}`;
      }

      lastRowIndex = currRowIndex;
    });

    navigator.clipboard.writeText(copyString);
  }

  getValueByCell(cell: HTMLTableCellElement): number | string | boolean {
    const selectFound: HTMLElement = cell.children[0].querySelector(CONSTANTS.SELECT_VALUE_ELEMENT);
    const inputFound: HTMLInputElement = cell.children[0].querySelector(
      CONSTANTS.FIELD_TYPES.INPUT,
    );
    if (selectFound) {
      return selectFound.innerHTML;
    }
    if (inputFound && inputFound.value) {
      return GetBooleanFromString(inputFound.value);
    }
    return Array.from(cell.children)
      .map((element: HTMLElement) => element.innerText)
      .join('');
  }

  get minorSelectedRowIndex(): number {
    return this.currentSelection?.minorCell.row;
  }

  get minorSelectedCellIndex(): number {
    return this.currentSelection?.minorCell.col;
  }
}
